/**
 * Protected route component for authentication-required routes
 */

import { forwardRef, useEffect } from 'react';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import { usePermissions } from '../hooks/usePermissions';
import type { ProtectedRouteProps } from '../types';
import { Center, Stack, Title, Text, ThemeIcon, Button } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';

/**
 * Protected route component for wrapping routes that require authentication
 *
 * @example
 * ```tsx
 * <ProtectedRoute
 *   requireAuth
 *   requiredRoles={['admin']}
 *   redirectTo="/login"
 *   fallback={<div>Loading...</div>}
 * >
 *   <AdminDashboard />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute = forwardRef<HTMLDivElement, ProtectedRouteProps>(
  (props, ref) => {
    const {
      children,
      fallback,
      requireAuth = true,
      requiredRoles,
      requiredPermissions,
      redirectTo = '/login',
    } = props;

    const defaultFallback = (
      <Center
        ref={ref}
        style={{ minHeight: 'calc(100vh - 100px)' }}
        px="md"
      >
        <Stack align="center" gap="xs" maw={420}>
          <ThemeIcon size={64} radius="xl" variant="light" color="red">
            <IconLock size={32} stroke={1.5} />
          </ThemeIcon>

          <Title order={2} ta="center" mt="sm">
            You do not have access
          </Title>

          <Text c="dimmed" ta="center" size="sm">
            You do not have permission to view this page. If you think this is a
            mistake, please contact your administrator.
          </Text>

          <Button
            variant="light"
            mt="md"
            onClick={() => window.history.back()}
          >
            Go back
          </Button>
        </Stack>
      </Center>
    );
    const activeFallback = fallback ?? defaultFallback;

    // Get auth state from SDK
    const { isAuthenticated, isLoading } = useEnrichedAuth();
    const { canAccess } = usePermissions();

    useEffect(() => {
      if (!isLoading && requireAuth && !isAuthenticated && redirectTo) {
        // Redirect to login
        window.location.href = redirectTo;
      }
    }, [isLoading, isAuthenticated, requireAuth, redirectTo]);

    // Show loading state
    if (isLoading) {
      return <div ref={ref}>Loading...</div>;
    }

    // Not authenticated
    if (requireAuth && !isAuthenticated) {
      return <div ref={ref}>{activeFallback}</div>;
    }

    // Check role/permission requirements.
    // requireAll = false → the user needs AT LEAST ONE of the listed roles
    // (and at least one of the listed permissions). This matches how
    // `requiredRoles` is documented everywhere else ("at least one of these
    // roles") and how the sidebar/domain visibility checks evaluate access.
    // Passing `true` here previously demanded the user hold EVERY listed role
    // simultaneously, which broke any multi-role route (e.g. ['BookingAdmin',
    // 'Admin']). Single-role routes are unaffected by this change.
    if (requiredRoles || requiredPermissions) {
      const hasAccess = canAccess(requiredRoles, requiredPermissions, false);

      if (!hasAccess) {
        return <div ref={ref}>{activeFallback}</div>;
      }
    }

    // Authenticated and authorized
    return <div ref={ref}>{children}</div>;
  }
);

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;
